import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import type { IconDefinition } from '@sorare/core/src/atoms/icons';
import { iconAlert2 } from '@sorare/core/src/atoms/icons/system/iconAlert2.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { iconPlus } from '@sorare/core/src/atoms/icons/system/iconPlus';
import { iconRefresh } from '@sorare/core/src/atoms/icons/system/iconRefresh.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import type { KycClaimState } from '@sorare/core/src/components/jonum/JonumVerificationDialog/useJonumVerificationGuard';

export type LineupsStickyFooterAction = 'restart' | 'claim' | 'setLineup';

export const getLineupsStickyFooterAction = ({
  aasmState,
  canCompose,
}: {
  aasmState: ManagerProgressionTaskState;
  canCompose: boolean;
}): LineupsStickyFooterAction | null => {
  if (aasmState === ManagerProgressionTaskState.CANCELED) {
    return 'restart';
  }
  if (aasmState === ManagerProgressionTaskState.COMPLETED) {
    return 'claim';
  }
  if (aasmState === ManagerProgressionTaskState.READY && canCompose) {
    return 'setLineup';
  }
  return null;
};

type LineupsStickyFooterCallbacks = {
  onRestart?: () => void;
  onClaim: () => void;
  onSetLineup: () => void;
};

type LineupsStickyFooterPresentation = {
  icon?: IconDefinition;
  label: ReactNode;
  background?: string;
  labelColor?: string;
  onClick?: () => void;
};

const getClaimPresentation = (
  onClaim: () => void,
  kycState?: KycClaimState
): LineupsStickyFooterPresentation => {
  if (kycState === 'pendingVerification') {
    return {
      icon: iconHourglass,
      label: <FormattedMessage defaultMessage="Pending verification" />,
      background: 'var(--c-nd-600-opaque)',
      labelColor: 'var(--c-black)',
    };
  }
  if (kycState === 'actionRequired') {
    return {
      icon: iconAlert2,
      label: (
        <FormattedMessage defaultMessage="Finish my verification and claim my reward" />
      ),
      background: 'var(--c-yellow-400)',
      labelColor: 'var(--c-black)',
      onClick: onClaim,
    };
  }
  return {
    icon: iconGift,
    label: <FormattedMessage defaultMessage="Claim prize" />,
    background: 'var(--c-blue-400)',
    onClick: onClaim,
  };
};

/** Shared icon / label / styling / handler for {@link LineupsStickyFooter} actions. */
export const getLineupsStickyFooterPresentation = (
  action: LineupsStickyFooterAction,
  {
    onRestart,
    onClaim,
    onSetLineup,
    kycState,
  }: LineupsStickyFooterCallbacks & { kycState?: KycClaimState }
): LineupsStickyFooterPresentation | undefined =>
  ({
    ...(onRestart
      ? {
          restart: {
            icon: iconRefresh,
            label: <FormattedMessage defaultMessage="Restart" />,
            onClick: onRestart,
          },
        }
      : {}),
    claim: getClaimPresentation(onClaim, kycState),
    setLineup: {
      label: <FormattedMessage defaultMessage="Add team now" />,
      icon: iconPlus,
      onClick: onSetLineup,
    },
  })[action];

type Props = LineupsStickyFooterCallbacks & {
  action: LineupsStickyFooterAction | null;
  banner?: ReactNode;
  kycState?: KycClaimState;
};

const StickyWrapper = styled(Vertical)`
  position: sticky;
  margin-top: auto;
  width: 100%;
  bottom: 0;
  max-width: 100%;
`;

const ButtonWrapper = styled(ButtonBase)`
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  text-align: center;
`;

const AddButton = styled.div`
  position: relative;
  padding: var(--unit);
  margin-top: -30px;
  width: 120px;
  overflow: clip;
  display: flex;
  justify-content: center;

  &::before {
    --size: 30px;
    --m: 0.6;
    --p: calc(var(--m) * var(--size));
    --R: calc(var(--size) * sqrt(var(--m) * var(--m) + 1));
    position: absolute;
    display: block;
    z-index: -1;
    content: '';
    top: calc(-1 * var(--intermediate-unit));
    left: 50%;
    width: 240px;
    height: 120px;
    background: var(--background, var(--c-black));
    transform: translateX(-60px);
    mask:
      radial-gradient(
          var(--R) at 50% calc(var(--size) + var(--p)),
          #000 99%,
          #0000 101%
        )
        calc(50% - 2 * var(--size)) 0 / calc(4 * var(--size)) 100%,
      radial-gradient(var(--R) at 50% calc(-1 * var(--p)), #0000 99%, #000 101%)
        50% var(--size) / calc(4 * var(--size)) 100% repeat-x;
  }
`;

const Label = styled(LabelM)`
  background-color: var(--background, var(--c-black));
  color: var(--label-color, inherit);
  width: 100%;
  padding: var(--triple-unit) var(--double-unit);
  margin-top: -30px;
`;

export const LineupsStickyFooter = ({
  action,
  banner,
  onRestart,
  onClaim,
  onSetLineup,
  kycState,
}: Props) => {
  const presentation = action
    ? getLineupsStickyFooterPresentation(action, {
        onRestart,
        onClaim,
        onSetLineup,
        kycState,
      })
    : undefined;

  if (!action) {
    return null;
  }

  return (
    <StickyWrapper center gap={5}>
      {banner}
      {!!presentation && (
        <ButtonWrapper
          as={presentation.onClick ? undefined : 'div'}
          style={
            {
              ...(presentation.background
                ? { '--background': presentation.background }
                : {}),
              ...(presentation.labelColor
                ? { '--label-color': presentation.labelColor }
                : {}),
            } as CSSProperties
          }
          onClick={presentation.onClick}
        >
          <AddButton>
            <IconButton
              asSpan
              icon={presentation.icon}
              size="large"
              color={presentation.background ? 'tertiary' : undefined}
            />
          </AddButton>
          <Label bold>{presentation.label}</Label>
        </ButtonWrapper>
      )}
    </StickyWrapper>
  );
};
